import React, {useMemo} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import InviteTeamMemberModal from "./InviteTeamMemberModal";
import {useTalentTeam} from "../../../providers/TalentTeamProvider";
import TeamMemberTable from "./TeamMemberTable";
import AddTeamMemberModal from "../../suppliers/components/AddTeamMemberModal";
import AdminAddTeamMemberModal from "./AdminAddTeamMemberModal";
import {useAuth} from "../../../providers/AuthProvider";
import {CheckCircleFilled, CloseCircleFilled} from "@ant-design/icons";
import TalentTeam from "../../../models/TalentTeam";

const TeamRosterStep = (props) => {

    const {auth} = useAuth();

    const {team, onChange, isUpdating} = useTalentTeam();
    const navigate = useNavigate();
    const params = useParams();

    const otherTeamMembers = TB.filter(team?.positions || [], pos => pos?.user?.id !== auth.id);

    // Counted off `positions` alone — the API already merges pending and rejected
    // invites into it, so adding `pending_invites` double-counted every invite.
    const positionsInvites = useMemo(() => {
        return team?.rosterSize?.() || 0
    }, [isUpdating, team]);

    const needsMoreMembers = positionsInvites < TalentTeam.MIN_ROSTER_SIZE;

    const rolesFilledForServices = team.rolesFilledForServices();
    const rolesMissingForServices = team.rolesMissingForServices();

    return (
        <div>
            <div className="form-title-section">
                <div className="step-title-content">
                    <div>
                        <div className="flex items-center pb-4">
                            <h3 className="form-title !mb-0">
                                Team Roster {positionsInvites > 0 ? `(${positionsInvites})` : ''}
                            </h3>
                        </div>

                        <div className="flex flex-row justify-between items-center pb-4">
                            <h5 className="form-subtitle">
                                Manage your team members
                            </h5>

                            <div className="flex flex-row items-center gap-3">
                                {(auth.canManageTalent() ? team.is_supplied_by_client : auth.canManageClientResourceTalent()) ?
                                    <AddTeamMemberModal/> :
                                    <InviteTeamMemberModal/>
                                }
                                {/* Talent managers get a direct-add option next to the
                                    invite flow. Renders null for everyone else. */}
                                <AdminAddTeamMemberModal/>
                            </div>

                        </div>

                    </div>
                </div>
            </div>

            {/* The step stays incomplete until the team has more than just its
                owner, so say so rather than leaving a silent red ✗ on the tab. */}
            {needsMoreMembers && (
                <div className="bg-white mb-5 px-6 py-4 border-l-4 border-warning">
                    <p className="text-sm">
                        A team needs at least <b>{TalentTeam.MIN_ROSTER_SIZE} people</b> before
                        this step can be completed. Invite or add{' '}
                        {TB.pluralize('more team member', TalentTeam.MIN_ROSTER_SIZE - positionsInvites, true)} to continue.
                    </p>
                    <p className="text-xs text-info mt-1">
                        Pending invites count towards this total — you don't have to wait for them to be accepted.
                    </p>
                </div>
            )}

            <TeamMemberTable hideAuth={false} hideInvite={true} split={false}/>


            {(rolesFilledForServices?.length > 0 || rolesMissingForServices?.length > 0) && (
                <div className="bg-white mt-5 px-6 py-7 flex flex-col gap-3">
                    <div>
                        <p className="text-base font-semibold mb-2">
                            Required Roles
                        </p>
                        <p className="text-xs">
                            Based upon the project types you selected, your team is missing {TB.pluralize("role", rolesMissingForServices?.length,true)} to form a complete, approved team.
                        </p>
                    </div>
                    {!!rolesFilledForServices?.length && (
                        <div className="flex flex-row flex-wrap gap-y-3 justify-start mt-3">
                            {TB.map(rolesFilledForServices, role => (
                                <div key={role.id} className="flex flex-row flex-nowrap items-center gap-3 basis-full sm:basis-1/2 md:basis-1/3 pr-2">
                                    <CheckCircleFilled className="text-success" style={{fontSize: 24}}/>
                                    <p className="whitespace-no-wrap font-medium text-sm">{role.name}</p>
                                </div>
                            ))}
                        </div>
                    )}
                    {!!rolesFilledForServices?.length && !!rolesMissingForServices?.length && (
                        <hr/>
                    )}
                    {!!rolesMissingForServices?.length && (
                        <div className="flex flex-row flex-wrap gap-y-3 justify-start">
                            {TB.map(rolesMissingForServices, role => (
                                <div key={role.id} className="flex flex-row flex-nowrap items-center gap-3 basis-full sm:basis-1/2 md:basis-1/3 pr-2">
                                    <CloseCircleFilled className="text-danger" style={{fontSize: 24}}/>
                                    <p className="whitespace-no-wrap font-medium text-sm">{role.name}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

        </div>
    );
}

export default TeamRosterStep;